/**
 * Aircraft paint schemes — the load-time face recolour
 * (docs/formats/shading-and-paint.md, VERIFIED by disassembly + memory dump).
 *
 * Fighter `.TBL` models do **not** ship their real colours. Every face named by
 * the model's `.CLG` carries an artist **colour-key marker** instead — one
 * garish palette index per `.CLG` group, the same six values in every fighter
 * (`PAINT_GROUP_MARKERS`). At load the engine overwrites both colour bytes
 * (+1 outline, +2 fill) of each listed face with the scheme colour for that
 * group. Baking a fighter without doing this is what produces the "clown
 * colours" — yellow fuselages and pure-blue wings — that never appear in game.
 *
 * The three tables that drive it live in `RB.EXE`'s data segment (and, for the
 * shell's 3D viewer, byte-identically in `PS.EXE`): a 17-entry palette of paint
 * colours, a model-type-to-scheme index, and the scheme records themselves plus
 * per-ace overrides. This module reads them out of the executable rather than
 * hard-coding them, so a bake stays reproducible from `data/original`.
 */

import { BinaryReader } from "./binary-reader.ts";

/**
 * `.CLG` group index -> the marker colour its faces carry in the shipped
 * `.TBL`. Exception-free across all 22 fighters / 3 206 face sides. Values
 * 192 (black, the national markings), 197 and 216 are *literal* colours and
 * never appear in a `.CLG` list.
 */
export const PAINT_GROUP_MARKERS: readonly number[] = [194, 255, 206, 211, 243, 224];

/** Number of paint groups — the same six as `.CLG`'s damage/collision zones. */
export const PAINT_GROUP_COUNT = 6;

/** `RB.EXE` DGROUP paragraph 0x4289, i.e. file offset of data-segment offset 0. */
const RB_DGROUP_BASE = 0x47090;
const SLOT_COLOURS_OFFSET = RB_DGROUP_BASE + 0x0a7d;
const SLOT_COLOUR_COUNT = 17;
const TYPE_TO_SCHEME_OFFSET = RB_DGROUP_BASE + 0x0a8e;
const MODEL_TYPE_COUNT = 33;
const ACE_RECORDS_DGROUP = 0x0aaf;
const ACE_RECORDS_OFFSET = RB_DGROUP_BASE + ACE_RECORDS_DGROUP;
const ACE_RECORD_SIZE = 8;
const ACE_RECORD_COUNT = 42;
const SCHEME_RECORDS_OFFSET = RB_DGROUP_BASE + 0x0bff;
const SCHEME_COUNT = 22;
const ACE_NAMES_OFFSET = RB_DGROUP_BASE + 0x0972;
const ACE_NAME_COUNT = 25;
const MODEL_NAMES_OFFSET = RB_DGROUP_BASE + 0x04ff;
/** `typeToScheme` value meaning "this model is never repainted" (bombers, recon, scenery). */
export const SCHEME_NONE = 22;
/** `aceOverride.marking` value meaning "no personal insignia". */
const MARKING_NONE = 0;

/** One 9-byte scheme record: six paint-table slots plus the list of aces that override them. */
export interface PaintScheme {
  /** Slot index into `slotColours`, one per `.CLG` group. */
  readonly slots: readonly number[];
  /** The named pilots whose own record replaces this scheme's slots. */
  readonly aces: readonly AceOverride[];
}

/** One 8-byte per-ace override: the historical personal scheme for one named pilot. */
export interface AceOverride {
  readonly aceId: number;
  readonly slots: readonly number[];
  /**
   * Personal-insignia index, or `undefined` for none. It selects a node within
   * the model's `stateChannel == 2` groups — the marking geometry — via the
   * per-instance state array (shading-and-paint.md §10.5). A player who is not
   * a named ace never has one, which is why the paint shop cannot pick an
   * insignia and the national markings survive any repaint.
   */
  readonly marking?: number;
}

/** Every table the recolour needs, read out of one `RB.EXE` image. */
export interface RbPaintTables {
  /** 17 palette indices, all `SHADES.DAT` shading anchors. */
  readonly slotColours: Uint8Array;
  /** Model type id (index into `modelNames`) -> scheme index, or `SCHEME_NONE`. */
  readonly typeToScheme: Uint8Array;
  /** 33 lower-case model base names, in type-id order (`"foke3"`, `"alba_d3"`, ...). */
  readonly modelNames: readonly string[];
  readonly schemes: readonly PaintScheme[];
  readonly aceOverrides: readonly AceOverride[];
  /** 25 ace surnames, indexed by ace id (`aceNames[0] === "Richthofen"`). */
  readonly aceNames: readonly string[];
}

function readNames(reader: BinaryReader, offset: number, count: number): string[] {
  reader.seek(offset);
  const names: string[] = [];
  for (let i = 0; i < count; i++) names.push(reader.cstring());
  return names;
}

function readAceOverrides(reader: BinaryReader): AceOverride[] {
  reader.seek(ACE_RECORDS_OFFSET);
  const overrides: AceOverride[] = [];
  for (let i = 0; i < ACE_RECORD_COUNT; i++) {
    const aceId = reader.u8();
    const slots = [...reader.bytes(PAINT_GROUP_COUNT)];
    const marking = reader.u8();
    overrides.push(marking === MARKING_NONE ? { aceId, slots } : { aceId, slots, marking: marking - 1 });
  }
  return overrides;
}

function readSchemes(reader: BinaryReader, overrides: readonly AceOverride[]): PaintScheme[] {
  reader.seek(SCHEME_RECORDS_OFFSET);
  const schemes: PaintScheme[] = [];
  let claimed = 0;
  for (let i = 0; i < SCHEME_COUNT; i++) {
    const aceCount = reader.u8();
    // Near pointer into the ace-override array; schemes are *not* in array
    // order, so the pointer is what picks the slice, not a running cursor.
    const acePointer = reader.u16();
    const slots = [...reader.bytes(PAINT_GROUP_COUNT)];
    const first = aceCount === 0 ? 0 : (acePointer - ACE_RECORDS_DGROUP) / ACE_RECORD_SIZE;
    if (!Number.isInteger(first) || first < 0 || first + aceCount > overrides.length) {
      throw new Error(`RB.EXE paint tables: scheme ${i} ace pointer 0x${acePointer.toString(16)} is out of the record array`);
    }
    schemes.push({ slots, aces: overrides.slice(first, first + aceCount) });
    claimed += aceCount;
  }
  if (claimed !== overrides.length) {
    throw new Error(`RB.EXE paint tables: scheme ace counts total ${claimed}, but ${overrides.length} records exist`);
  }
  return schemes;
}

/**
 * Reads the paint tables from an `RB.EXE` image. Offsets are fixed for the
 * shipped build; rather than pin a hash, this validates the three properties
 * that would break first if the offsets were wrong — the first ace is
 * Richthofen, the first model type is `morane`, and the scheme records' ace
 * counts tile the ace-override array exactly.
 */
export function readRbPaintTables(source: ArrayBufferLike | ArrayBufferView): RbPaintTables {
  const reader = new BinaryReader(source);
  reader.seek(SLOT_COLOURS_OFFSET);
  const slotColours = reader.bytes(SLOT_COLOUR_COUNT).slice();
  reader.seek(TYPE_TO_SCHEME_OFFSET);
  const typeToScheme = reader.bytes(MODEL_TYPE_COUNT).slice();

  const aceNames = readNames(reader, ACE_NAMES_OFFSET, ACE_NAME_COUNT);
  const modelNames = readNames(reader, MODEL_NAMES_OFFSET, MODEL_TYPE_COUNT);
  if (aceNames[0] !== "Richthofen" || modelNames[0] !== "morane") {
    throw new Error(
      `RB.EXE paint tables: expected ace 0 "Richthofen" and model type 0 "morane", ` +
        `got "${aceNames[0]}" / "${modelNames[0]}" — this is not the build these offsets describe`,
    );
  }

  const aceOverrides = readAceOverrides(reader);
  const schemes = readSchemes(reader, aceOverrides);
  return { slotColours, typeToScheme, modelNames, schemes, aceOverrides, aceNames };
}

/**
 * The six palette indices a given model type is painted with, in `.CLG` group
 * order, or `undefined` for a model the engine never repaints. Pass `aceId`
 * for a named pilot's aircraft; the override only applies if that ace is listed
 * on the type's own scheme (Richthofen's all-red slots apply to his Albatros
 * and Dr.I, not to every aircraft in the game).
 */
export function resolvePaintColours(
  tables: RbPaintTables,
  modelTypeId: number,
  aceId?: number,
): number[] | undefined {
  const schemeIndex = tables.typeToScheme[modelTypeId];
  if (schemeIndex === undefined || schemeIndex >= tables.schemes.length) return undefined;
  const scheme = tables.schemes[schemeIndex]!;
  // An ace flies several types and is painted differently on each, so the
  // override must come from *this* scheme's list, not a global lookup.
  const override = aceId === undefined ? undefined : scheme.aces.find((ace) => ace.aceId === aceId);
  return (override?.slots ?? scheme.slots).map((slot) => tables.slotColours[slot] ?? 0);
}

/** Looks up a model's type id from its base name (`"FOKE3"`, `"foke3"`), or `undefined`. */
export function modelTypeId(tables: RbPaintTables, baseName: string): number | undefined {
  const index = tables.modelNames.indexOf(baseName.toLowerCase());
  return index < 0 ? undefined : index;
}

/**
 * `.CLG` group indices the paint shop's **wings** choice drives: the wings
 * (2) and the wing underside (3). `RB.EXE` seg `133f:0035` writes the wings
 * slot to `DGROUP 0x5011`/`0x5012`, which are groups 2 and 3 of the six-byte
 * player slot array at `0x500F`.
 */
export const PAINT_SHOP_WING_GROUPS: readonly number[] = [2, 3];

/**
 * `.CLG` group indices the **fuselage** choice drives: main fabric (0),
 * second fabric/trim (1), fuselage (4) and cowling/metalwork (5) — `0x500F`,
 * `0x5010`, `0x5013` and `0x5014`. Group 5 is special: on the *seeding* call
 * the routine puts the factory scheme's own cowling slot back (see
 * `playerPaintSlots`), so an unpainted personal aircraft keeps its metalwork.
 */
export const PAINT_SHOP_BODY_GROUPS: readonly number[] = [0, 1, 4, 5];

/** The `.CLG` group whose colour the seeding path restores from the factory scheme. */
export const PAINT_SHOP_COWLING_GROUP = 5;

/**
 * The eight colours the original's `WHICH COLOR?` menu offers, in menu order.
 *
 * They are **the first eight entries of the 17-slot paint table**: the pilot
 * record's two paint bytes are slot indices, and `expandSlots`
 * (`RB.EXE` seg `133f:000F`) maps them through `DGROUP 0x0A7D` exactly as it
 * does a factory scheme's. Slots 8-16 are the extra ramp anchors only the
 * factory and ace schemes reach.
 *
 * The names are `PS.EXE`'s own string block; that they line up one-for-one
 * with slots 0-7's palette colours — 106 blue, 30 red, 18 ochre, 94 dark
 * green, 42 tan, 70 near-black, 76 mid grey, 84 near-white — is what pins the
 * mapping, and `rb-paint.test.ts` asserts it against the real executable.
 */
export const PAINT_SHOP_COLOUR_NAMES: readonly string[] = [
  "Blue",
  "Red",
  "Yellow",
  "Olive",
  "Tan",
  "Black",
  "Grey",
  "White",
];

/** Number of paint-shop colours; slots `0 .. PAINT_SHOP_SLOT_COUNT - 1` of the paint table. */
export const PAINT_SHOP_SLOT_COUNT = PAINT_SHOP_COLOUR_NAMES.length;

/**
 * `BARON.PAL` index of each paint-shop colour, and its `SHADES.DAT` shading
 * half-width — the two numbers a renderer needs to repaint a face and keep
 * re-shading it correctly (the new anchor's ramp is *not* the old one's).
 *
 * Transcribed, for the same reason `SHIPPED_SHADE_DELTAS` is: the browser
 * needs it and it is sixteen bytes of engine constant.
 * `readRbPaintTables` + `decodeShadesDat` remain the authority —
 * `rb-paint.test.ts` reads the real `RB.EXE` and `SHADES.DAT` and asserts
 * this table equals them.
 */
export const SHIPPED_PAINT_SHOP_COLOURS: readonly { readonly index: number; readonly halfWidth: number }[] = [
  { index: 106, halfWidth: 6 }, // Blue    100..111
  { index: 30, halfWidth: 6 }, //  Red      24..35
  { index: 18, halfWidth: 6 }, //  Yellow   12..23
  { index: 94, halfWidth: 6 }, //  Olive    88..99
  { index: 42, halfWidth: 6 }, //  Tan      36..47
  { index: 70, halfWidth: 6 }, //  Black    64..75
  { index: 76, halfWidth: 4 }, //  Grey     72..79
  { index: 84, halfWidth: 4 }, //  White    80..87
];

/** The player's two paint-shop choices, as slot indices into the paint table. */
export interface PlayerPaintChoice {
  /** Slot painted onto `.CLG` groups 2 and 3. */
  readonly wings: number;
  /** Slot painted onto groups 0, 1, 4 and, once repainted, 5. */
  readonly fuselage: number;
}

/**
 * What the paint shop seeds a freshly issued personal aircraft with:
 * `RB.EXE` seg `133f:0035` called with the `(0xFF, 0xFF)` sentinel resolves
 * the aircraft's own scheme (ace override included) and takes the **wings**
 * choice from its group-2 slot and the **fuselage** choice from its group-4
 * slot.
 */
export function defaultPlayerPaint(schemeSlots: readonly number[]): PlayerPaintChoice {
  return { wings: schemeSlots[2] ?? 0, fuselage: schemeSlots[4] ?? 0 };
}

/**
 * The six slots the engine stores at `DGROUP 0x500F`, i.e. what the two
 * choices expand to per `.CLG` group. `cowlingSlot` reproduces the seeding
 * path's last write (`if (di) 0x5014 = di[5]`): pass the factory scheme's
 * group-5 slot for an aircraft the player has not repainted, and omit it once
 * he has — the original lets the fuselage colour take the cowling from then
 * on.
 */
export function playerPaintSlots(choice: PlayerPaintChoice, cowlingSlot?: number): number[] {
  const slots: number[] = Array.from({ length: PAINT_GROUP_COUNT }, () => choice.fuselage);
  for (const group of PAINT_SHOP_WING_GROUPS) slots[group] = choice.wings;
  if (cowlingSlot !== undefined) slots[PAINT_SHOP_COWLING_GROUP] = cowlingSlot;
  return slots;
}

/**
 * The six palette indices the player's aircraft is painted with — the
 * `133f:0035` -> `0x500F` -> `expandSlots` path end to end. Slots outside the
 * table resolve to `0`, matching the engine's unchecked index.
 */
export function resolvePlayerPaintColours(
  tables: RbPaintTables,
  choice: PlayerPaintChoice,
  cowlingSlot?: number,
): number[] {
  return playerPaintSlots(choice, cowlingSlot).map((slot) => tables.slotColours[slot] ?? 0);
}

/**
 * `ds:0x0A6C` — the 17-byte slot canonicaliser the original's 2D screens use
 * to re-tint the flat aircraft artwork the paint shop's `SAVE` writes into
 * (docs/formats/shading-and-paint.md §10.4, VERIFIED). `setPlayerPaint`
 * (`2d94:000A`) pushes a chosen slot through this array before storing it;
 * the five entries that move send a narrow upper-half anchor to its ramp's
 * full span (7 -> 13, 8 -> 1, 10 -> 0, 14 -> 3), everything else is identity.
 *
 * Recovered whole from the executable, but its consumer — the `2d94:057A`
 * 12-index-window substitution into the `VGACOL.DAT` fill/outline globals
 * (`[0x2E9D]`/`[0x2E9E]`) — is not: `VGACOL.DAT`'s 75 logical-colour slots are
 * only partially mapped to on-screen elements (§5, PARTIAL), so which pixels
 * the window actually touches is still unknown. This function is exactly
 * (and only) the recovered half.
 */
export const PAINT_SLOT_CANON: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 13, 1, 9, 0, 11, 12, 13, 3, 15, 16];

/**
 * A paint-table slot as the 2D screens' canonicaliser sees it. Out-of-range
 * slots pass through unchanged — the table has no entry for them, and the
 * original's own lookup is unchecked the same way `resolvePlayerPaintColours`
 * treats an out-of-range slot as a no-op index.
 */
export function canonicalizePaintSlot(slot: number): number {
  return PAINT_SLOT_CANON[slot] ?? slot;
}

/** A repaint expressed in palette indices; either half may be left as the factory's. */
export interface PersonalPaint {
  /** Palette index for `.CLG` groups 2 and 3, or `undefined` to keep the factory's. */
  readonly wings?: number;
  /** Palette index for groups 0, 1, 4 (and 5, once anything is painted). */
  readonly fuselage?: number;
}

/**
 * The six group colours of a personal aircraft, given the factory scheme it
 * was issued in and whatever the pilot has painted since — the same result as
 * `playerPaintSlots`, in palette indices, so a consumer holding a *baked*
 * scheme needs no access to the slot table.
 *
 * The cowling rule is the original's and is easy to get wrong: an aircraft
 * nobody has painted keeps its factory metalwork (the `(0xFF, 0xFF)` seeding
 * call restores group 5 from the scheme), but the first trip to the paint shop
 * calls `133f:0035` with real arguments, and from then on group 5 follows the
 * **fuselage** colour like the rest of the body.
 */
export function personalSchemeColours(schemeColours: readonly number[], paint: PersonalPaint): number[] {
  const wings = paint.wings ?? schemeColours[2] ?? 0;
  const fuselage = paint.fuselage ?? schemeColours[4] ?? 0;
  const untouched = paint.wings === undefined && paint.fuselage === undefined;
  const cowling = untouched ? (schemeColours[PAINT_SHOP_COWLING_GROUP] ?? fuselage) : fuselage;
  const colours: number[] = Array.from({ length: PAINT_GROUP_COUNT }, () => fuselage);
  for (const group of PAINT_SHOP_WING_GROUPS) colours[group] = wings;
  colours[PAINT_SHOP_COWLING_GROUP] = cowling;
  return colours;
}

/**
 * Expands a resolved six-colour scheme into the per-face rewrite the engine
 * performs: face record offset -> palette index, for both the outline (+1) and
 * fill (+2) bytes. `clgGroups` are the `.CLG` offset lists; `modelBase` is the
 * object's header offset (`TblObject.headerOffset`, 8 for every fighter), which
 * is what `.CLG` offsets are relative to.
 */
export function paintedFaceColours(
  clgGroups: readonly (readonly number[])[],
  modelBase: number,
  colours: readonly number[],
): Map<number, number> {
  const painted = new Map<number, number>();
  clgGroups.forEach((offsets, group) => {
    const colour = colours[group];
    if (colour === undefined) return;
    for (const offset of offsets) painted.set(modelBase + offset, colour);
  });
  return painted;
}

/**
 * The same expansion, but keeping *which* group claimed each face instead of
 * the colour it resolved to: face record offset -> `.CLG` group index.
 *
 * A scheme's six colours repeat (most fighters paint fabric, wings and
 * fuselage the same index), so a baked face's colour cannot be inverted back
 * to a group. Carrying the group itself is what lets a renderer repaint a
 * model it has already loaded — the player's paint shop, where wings and
 * fuselage must move apart from a scheme that had them equal.
 */
export function paintedFaceGroups(
  clgGroups: readonly (readonly number[])[],
  modelBase: number,
): Map<number, number> {
  const groups = new Map<number, number>();
  clgGroups.forEach((offsets, group) => {
    for (const offset of offsets) groups.set(modelBase + offset, group);
  });
  return groups;
}
